import { useEffect, useRef, useState } from 'react';

type UseDelayedContentVisibilityParams = {
  delay?: number;
  isCollapsed: boolean;
};

/**
 * Hook to delay content visibility after a sidebar expands.
 * Hides content immediately on collapse, shows after delay on expand.
 * Prevents visual glitches during width animations.
 */
export function useDelayedContentVisibility(params: UseDelayedContentVisibilityParams): boolean {
  const { delay = 200, isCollapsed } = params;
  const [isContentVisible, setIsContentVisible] = useState(!isCollapsed);
  const prevIsCollapsedRef = useRef(isCollapsed);

  useEffect(() => {
    const wasCollapsed = prevIsCollapsedRef.current;
    prevIsCollapsedRef.current = isCollapsed;

    if (isCollapsed) {
      // Hide content immediately when collapsing
      setIsContentVisible(false);
    } else if (wasCollapsed) {
      // Only delay when transitioning from collapsed to expanded
      const timeoutId = setTimeout(() => setIsContentVisible(true), delay);
      return () => clearTimeout(timeoutId);
    }
    // If not collapsed and wasn't collapsed before (initial load), do nothing
    // Content is already visible from the initial useState
  }, [delay, isCollapsed]);

  return isContentVisible;
}
